import { useState, useEffect } from "react";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";

export interface InventoryField {
  id: string | number;
  label: string;
  fieldKey?: string;
  type: string;
  required: boolean;
  showInList: boolean;
  showInForm: boolean;
  active: boolean;
  options?: string[];
  placeholder?: string;
  order: number;
}

const inventoryFieldTypeOptions = [
  "Text Input",
  "Number Input",
  "Currency",
  "Dropdown",
  "Multi-select",
  "Date",
  "Toggle",
  "File Upload",
  "Phone",
  "Email",
  "Textarea",
];

const inventoryIndustryExamples = [
  {
    id: "real-estate",
    name: "Real Estate",
    emoji: "🏢",
    fields: [
      { label: "Unit Number", type: "Text Input", placeholder: "e.g. A-101" },
      { label: "Floor", type: "Number Input", placeholder: "e.g. 1" },
      { label: "Area (sq.ft)", type: "Number Input", placeholder: "e.g. 950" },
      { label: "Price", type: "Currency", placeholder: "e.g. 1.8 Cr" },
      { label: "Status", type: "Dropdown", options: ["Available", "Booked", "Sold", "On Hold"] },
    ],
  },
  {
    id: "manufacturing",
    name: "Manufacturing",
    emoji: "🏭",
    fields: [
      { label: "Product Code", type: "Text Input", placeholder: "e.g. PRD-001" },
      { label: "Batch Number", type: "Text Input", placeholder: "e.g. BATCH-2026" },
      { label: "Stock Qty", type: "Number Input", placeholder: "e.g. 50" },
      { label: "Unit Cost", type: "Currency", placeholder: "e.g. 2,500" },
      { label: "Status", type: "Dropdown", options: ["In Stock", "Low Stock", "Out of Stock"] },
    ],
  },
  {
    id: "education",
    name: "Education",
    emoji: "🎓",
    fields: [
      { label: "Course Name", type: "Text Input", placeholder: "e.g. Full Stack Dev" },
      { label: "Duration", type: "Text Input", placeholder: "e.g. 6 Months" },
      { label: "Fee", type: "Currency", placeholder: "e.g. 45,000" },
      { label: "Seats Available", type: "Number Input", placeholder: "e.g. 30" },
      { label: "Status", type: "Dropdown", options: ["Open", "Full", "Completed"] },
    ],
  },
  {
    id: "healthcare",
    name: "Healthcare",
    emoji: "🏥",
    fields: [
      { label: "Package Name", type: "Text Input", placeholder: "e.g. Full Body Checkup" },
      { label: "Duration", type: "Text Input", placeholder: "e.g. 1 Day" },
      { label: "Price", type: "Currency", placeholder: "e.g. 4,999" },
      { label: "Status", type: "Dropdown", options: ["Available", "Booked", "Closed"] },
    ],
  },
  {
    id: "saas",
    name: "SaaS / IT",
    emoji: "💻",
    fields: [
      { label: "Plan Name", type: "Text Input", placeholder: "e.g. Enterprise Tier" },
      { label: "Billing Cycle", type: "Dropdown", options: ["Monthly", "Quarterly", "Annual"] },
      { label: "Price", type: "Currency", placeholder: "e.g. 999" },
      { label: "Seats", type: "Number Input", placeholder: "e.g. 10" },
      { label: "Status", type: "Dropdown", options: ["Active", "Trial", "Cancelled"] },
    ],
  },
  {
    id: "retail",
    name: "Retail",
    emoji: "🛍️",
    fields: [
      { label: "Product Name", type: "Text Input", placeholder: "e.g. Cotton Shirt" },
      { label: "SKU", type: "Text Input", placeholder: "e.g. SK-1002" },
      { label: "Stock Qty", type: "Number Input", placeholder: "e.g. 100" },
      { label: "Price", type: "Currency", placeholder: "e.g. 1,299" },
      { label: "Status", type: "Dropdown", options: ["In Stock", "Out of Stock"] },
    ],
  },
];

const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};
const tempId = () => generateUUID();
const toKey = (l: string) =>
  l
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^_|_$/g, "");

const Toggle = ({
  checked,
  onChange,
  color = "bg-indigo-600",
}: {
  checked: boolean;
  onChange: () => void;
  color?: string;
}) => (
  <button
    type="button"
    onClick={onChange}
    className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 overflow-hidden ${
      checked ? color : "bg-gray-300"
    }`}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
        checked ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

type Props = {
  inventoryFields: InventoryField[];
  onFieldsChange: (fields: InventoryField[]) => void;
  onSave?: (fields: InventoryField[]) => Promise<void>;
};

export default function InventoryFieldsBuilder({
  inventoryFields: propFields,
  onFieldsChange,
  onSave,
}: Props) {
  const { showToast } = useToast();
  const [fields, setFields] = useState<InventoryField[]>(propFields || []);
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState<InventoryField | null>(null);
  const [showExamples, setShowExamples] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newField, setNewField] = useState({
    label: "",
    type: "Text Input",
    required: false,
    showInList: false,
    showInForm: true,
    options: [] as string[],
    placeholder: "",
  });
  const [newOption, setNewOption] = useState("");

  useEffect(() => {
    setFields(propFields || []);
  }, [propFields]);

  const updateFields = (u: InventoryField[], triggerSave: boolean = false) => {
    setFields(u);
    onFieldsChange(u);
    if (triggerSave && onSave) {
      onSave(u);
    }
  };

  const handleToggle = (
    id: string | number,
    k: "required" | "showInList" | "showInForm" | "active"
  ) => {
    const updated = fields.map((f) =>
      String(f.id) === String(id) ? { ...f, [k]: !f[k] } : f
    );
    updateFields(updated, true);
  };

  const handleDelete = async (id: string | number) => {
    const updated = fields.filter((f) => String(f.id) !== String(id));
    updateFields(updated, true);
    try {
      if (id && !String(id).startsWith("tmp-") && !String(id).startsWith("field-")) {
        await fetchApi(`/projects/settings/inventory-fields/${id}`, { method: "DELETE" });
      }
    } catch {
      // Handled gracefully
    }
    showToast("Field removed & saved!", "success");
  };

  const handleAddOpt = () => {
    if (newOption.trim()) {
      setNewField((p) => ({ ...p, options: [...p.options, newOption.trim()] }));
      setNewOption("");
    }
  };

  const handleRemOpt = (i: number) => {
    setNewField((p) => ({
      ...p,
      options: p.options.filter((_, idx) => idx !== i),
    }));
  };

  const resetForm = () => {
    setNewField({
      label: "",
      type: "Text Input",
      required: false,
      showInList: false,
      showInForm: true,
      options: [],
      placeholder: "",
    });
    setNewOption("");
    setEditingField(null);
  };

  const openEdit = (f: InventoryField) => {
    setEditingField(f);
    setNewField({
      label: f.label,
      type: f.type,
      required: f.required,
      showInList: f.showInList,
      showInForm: f.showInForm,
      options: f.options ? [...f.options] : [],
      placeholder: f.placeholder || "",
    });
    setShowModal(true);
  };

  const handleSaveField = () => {
    if (!newField.label.trim()) {
      showToast("Please enter a field label", "error");
      return;
    }

    let updatedList: InventoryField[];
    if (editingField) {
      updatedList = fields.map((f) =>
        String(f.id) === String(editingField.id)
          ? {
              ...f,
              label: newField.label.trim(),
              fieldKey: toKey(newField.label),
              type: newField.type,
              required: newField.required,
              showInList: newField.showInList,
              showInForm: newField.showInForm,
              options: newField.options.length > 0 ? newField.options : undefined,
              placeholder: newField.placeholder || undefined,
            }
          : f
      );
      updateFields(updatedList, true);
      showToast("Field updated & saved!", "success");
    } else {
      const maxOrder = fields.reduce((m, f) => {
        const ord = Number(f.order);
        return Number.isFinite(ord) && ord < 100000 ? Math.max(m, ord) : m;
      }, 0);
      const createdField: InventoryField = {
        id: tempId(),
        label: newField.label.trim(),
        fieldKey: toKey(newField.label),
        type: newField.type,
        required: newField.required,
        showInList: newField.showInList,
        showInForm: newField.showInForm,
        active: true,
        options: newField.options.length > 0 ? newField.options : undefined,
        placeholder: newField.placeholder || undefined,
        order: maxOrder + 1,
      };
      updatedList = [...fields, createdField];
      updateFields(updatedList, true);
      showToast("Field added & saved!", "success");
    }
    resetForm();
    setShowModal(false);
  };

  const handleUseExample = (ex: typeof inventoryIndustryExamples[0]) => {
    const newFields: InventoryField[] = ex.fields.map((f: any, i: number) => ({
      id: generateUUID(),
      label: f.label,
      fieldKey: toKey(f.label),
      type: f.type,
      required: i === 0 || f.label === "Status",
      showInList: true,
      showInForm: true,
      active: true,
      order: i + 1,
      options:
        f.options ||
        (f.label === "Status"
          ? ["Available", "Booked", "Sold", "On Hold"]
          : undefined),
      placeholder: f.placeholder || "",
    }));
    updateFields(newFields, true);
    setShowExamples(false);
    showToast(`${ex.name} template fields applied & saved!`, "success");
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      if (onSave) {
        await onSave(fields);
      }
    } catch {
      // Toast handled by onSave
    } finally {
      setIsSaving(false);
    }
  };

  const needsOptions =
    newField.type === "Dropdown" || newField.type === "Multi-select";

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-1">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Project Inventory Fields
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Define fields for items inside your projects — products, units,
            courses, packages etc.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-all active:scale-[0.98] whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line" /> Add Field
        </button>
      </div>

      <button
        type="button"
        onClick={() => setShowExamples(!showExamples)}
        className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium mt-2 mb-2 cursor-pointer"
      >
        See examples by industry{" "}
        <i className={`ri-arrow-${showExamples ? "up" : "down"}-s-line`} />
      </button>

      {showExamples && (
        <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-4 mb-4">
          <p className="text-sm font-semibold text-gray-900 mb-1">
            Inventory Field Examples
          </p>
          <p className="text-xs text-gray-500 mb-4">
            Click any to pre-fill your inventory fields.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {inventoryIndustryExamples.map((ex) => (
              <div
                key={ex.id}
                className="bg-white rounded-lg border border-gray-200 p-3 hover:border-indigo-400 transition-colors"
              >
                <p className="text-sm font-semibold text-gray-900">
                  {ex.emoji} {ex.name}
                </p>
                <div className="mt-1.5 space-y-0.5">
                  {ex.fields.map((f, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-gray-300" />
                      <span className="text-xs text-gray-600">{f.label}</span>
                      <span className="text-[10px] text-gray-400">
                        ({f.type})
                      </span>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => handleUseExample(ex)}
                  className="w-full mt-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium whitespace-nowrap cursor-pointer transition-all active:scale-[0.98]"
                >
                  Use These Fields
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table header */}
      <div className="grid grid-cols-12 gap-2 px-1 py-2 bg-gray-50 rounded-lg text-[11px] font-semibold uppercase text-gray-500 tracking-wider mb-1">
        <div className="col-span-3">Field Name</div>
        <div className="col-span-2">Type</div>
        <div className="col-span-1 text-center">Req</div>
        <div className="col-span-2 text-center">In List</div>
        <div className="col-span-2 text-center">In Form</div>
        <div className="col-span-1 text-center">Active</div>
        <div className="col-span-1" />
      </div>

      <div>
        {fields.map((field) => (
          <div
            key={String(field.id)}
            className={`grid grid-cols-12 gap-2 px-1 py-3 border-b border-gray-100 items-center hover:bg-gray-50/50 ${
              !field.active ? "opacity-60" : ""
            }`}
          >
            <div className="col-span-3 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {field.label}
              </p>
              {field.options && field.options.length > 0 && (
                <p className="text-[10px] text-gray-400 truncate">
                  {field.options.slice(0, 3).join(", ")}
                  {field.options.length > 3 ? "..." : ""}
                </p>
              )}
            </div>
            <div className="col-span-2">
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded whitespace-nowrap">
                {field.type}
              </span>
            </div>
            <div className="col-span-1 flex justify-center">
              <Toggle
                checked={field.required}
                onChange={() => handleToggle(field.id, "required")}
                color="bg-red-500"
              />
            </div>
            <div className="col-span-2 flex justify-center">
              <Toggle
                checked={field.showInList}
                onChange={() => handleToggle(field.id, "showInList")}
                color="bg-blue-500"
              />
            </div>
            <div className="col-span-2 flex justify-center">
              <Toggle
                checked={field.showInForm}
                onChange={() => handleToggle(field.id, "showInForm")}
                color="bg-indigo-500"
              />
            </div>
            <div className="col-span-1 flex justify-center">
              <Toggle
                checked={field.active}
                onChange={() => handleToggle(field.id, "active")}
                color="bg-green-500"
              />
            </div>
            <div className="col-span-1 flex justify-center gap-0.5">
              <button
                type="button"
                onClick={() => openEdit(field)}
                className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 cursor-pointer"
                title="Edit field"
              >
                <i className="ri-pencil-line text-xs" />
              </button>
              <button
                type="button"
                onClick={() => handleDelete(field.id)}
                className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer"
                title="Delete field"
              >
                <i className="ri-delete-bin-line text-xs" />
              </button>
            </div>
          </div>
        ))}
        {fields.length === 0 && (
          <div className="py-8 text-center text-sm text-gray-400">
            No inventory fields yet. Click "+ Add Field" or use an industry example above.
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => {
          resetForm();
          setShowModal(true);
        }}
        className="w-full mt-4 py-3 rounded-lg border border-dashed border-indigo-300 text-sm font-medium text-indigo-600 hover:bg-indigo-50 flex items-center justify-center gap-1.5 whitespace-nowrap cursor-pointer"
      >
        <i className="ri-add-line" /> Add Inventory Field
      </button>
      <button
        type="button"
        onClick={handleSaveAll}
        disabled={isSaving}
        className="w-full mt-3 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold active:scale-[0.98] flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
      >
        {isSaving ? (
          <>
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Saving...
          </>
        ) : (
          "Save Inventory Fields"
        )}
      </button>

      {/* Add/Edit Modal */}
      {showModal && (
        <>
          <div
            className="fixed inset-0 bg-black/20 z-40"
            onClick={() => {
              setShowModal(false);
              resetForm();
            }}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900">
                  {editingField ? "Edit" : "Add"} Inventory Field
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer"
                >
                  <i className="ri-close-line" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Field Label *
                  </label>
                  <input
                    type="text"
                    value={newField.label}
                    onChange={(e) =>
                      setNewField((p) => ({ ...p, label: e.target.value }))
                    }
                    placeholder="e.g. Unit Number, SKU, Course Name"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    autoFocus
                  />
                  {newField.label.trim() && (
                    <p className="text-[10px] text-gray-400 mt-1">
                      Key:{" "}
                      <code className="bg-gray-100 px-1 rounded">
                        {toKey(newField.label)}
                      </code>
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Field Type *
                  </label>
                  <select
                    value={newField.type}
                    onChange={(e) =>
                      setNewField((p) => ({
                        ...p,
                        type: e.target.value,
                        options: [],
                      }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white"
                  >
                    {inventoryFieldTypeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Placeholder
                  </label>
                  <input
                    type="text"
                    value={newField.placeholder}
                    onChange={(e) =>
                      setNewField((p) => ({
                        ...p,
                        placeholder: e.target.value,
                      }))
                    }
                    placeholder="Shown inside the input field"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                </div>
                {needsOptions && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Options
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={newOption}
                        onChange={(e) => setNewOption(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddOpt();
                          }
                        }}
                        placeholder="Add option..."
                        className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      />
                      <button
                        type="button"
                        onClick={handleAddOpt}
                        className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium whitespace-nowrap cursor-pointer"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {newField.options.map((opt, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs"
                        >
                          {opt}
                          <button
                            type="button"
                            onClick={() => handleRemOpt(i)}
                            className="text-indigo-400 hover:text-red-500 cursor-pointer"
                          >
                            <i className="ri-close-line" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {[
                  {
                    k: "required",
                    l: "Required",
                    d: "Must be filled",
                    c: "bg-red-500",
                  },
                  {
                    k: "showInList",
                    l: "Show in list",
                    d: "Column in table",
                    c: "bg-blue-500",
                  },
                  {
                    k: "showInForm",
                    l: "Show in form",
                    d: "When adding item",
                    c: "bg-indigo-500",
                  },
                ].map(({ k, l, d, c }) => (
                  <div key={k} className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-medium text-gray-700">
                        {l}
                      </span>
                      <p className="text-[10px] text-gray-400">{d}</p>
                    </div>
                    <Toggle
                      checked={(newField as any)[k]}
                      onChange={() =>
                        setNewField((p) => ({ ...p, [k]: !(p as any)[k] }))
                      }
                      color={c}
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveField}
                  className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  {editingField ? "Update" : "Add"} Field
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
